import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MapPin, Globe, Twitter, Linkedin, Instagram,
  MessageSquare, EyeOff, Pencil, Save, X, Plus,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { BadgeRow } from "../../components/BadgeChip";

function fmtJoined(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      year: "numeric", month: "long",
    });
  } catch (e) { return ""; }
}

function TierBadge({ tier, role }) {
  if (role === "admin")
    return <span className="text-[10px] px-1.5 py-0.5 bg-saffron text-navy rounded font-medium uppercase tracking-wider">Admin</span>;
  if (tier === "lifetime")
    return <span className="text-[10px] px-1.5 py-0.5 bg-saffron/15 text-saffron rounded font-medium uppercase tracking-wider">Lifetime</span>;
  if (tier === "annual" || tier === "supporting" || tier === "standard")
    return <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/15 text-blue-700 rounded font-medium uppercase tracking-wider">Annual</span>;
  return <span className="text-[10px] px-1.5 py-0.5 bg-navy/10 text-navy/65 rounded font-medium uppercase tracking-wider">Member</span>;
}

// ──────────────────────────────────────────────────────────────────────────────
// View
// ──────────────────────────────────────────────────────────────────────────────
export default function MemberProfile() {
  const { membershipId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isMe = user?.membership_id === membershipId;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/community/profile/${encodeURIComponent(membershipId)}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [membershipId]);

  if (loading) {
    return <div className="p-12 text-center text-navy/60" data-testid="member-profile-loading">Loading profile…</div>;
  }
  if (!data?.profile) {
    return (
      <div className="px-6 py-12 max-w-xl mx-auto text-center" data-testid="member-profile-notfound">
        <p className="text-navy/65">We couldn't find that member.</p>
        <Link to="/platform/community/members" className="mt-4 inline-block text-saffron hover:text-saffron-light">
          Back to directory →
        </Link>
      </div>
    );
  }

  const p = data.profile;
  const threads = data.threads || [];

  // Hidden profile (opted-out)
  if (p.directory_opt_out) {
    return (
      <div className="px-6 py-12 max-w-xl mx-auto text-center" data-testid="member-profile-hidden">
        <div className="w-16 h-16 mx-auto rounded-full bg-navy/10 text-navy/40 flex items-center justify-center">
          <EyeOff size={28} />
        </div>
        <h1 className="font-display text-2xl text-navy mt-4">{p.name || "Member"}</h1>
        <p className="text-navy/55 mt-2 text-sm">This member has chosen to keep their profile private.</p>
        <Link to="/platform/community/members" className="mt-6 inline-block text-saffron hover:text-saffron-light">
          Back to directory →
        </Link>
      </div>
    );
  }

  const photo = p.photo_data_url || p.photo_url;
  const social = [
    p.website && { Icon: Globe,    href: p.website.startsWith("http") ? p.website : `https://${p.website}`, label: "Website" },
    p.social_twitter && { Icon: Twitter, href: `https://twitter.com/${p.social_twitter.replace(/^@/, "")}`, label: `@${p.social_twitter.replace(/^@/, "")}` },
    p.social_linkedin && { Icon: Linkedin, href: p.social_linkedin.startsWith("http") ? p.social_linkedin : `https://linkedin.com/in/${p.social_linkedin}`, label: "LinkedIn" },
    p.social_instagram && { Icon: Instagram, href: `https://instagram.com/${p.social_instagram.replace(/^@/, "")}`, label: `@${p.social_instagram.replace(/^@/, "")}` },
  ].filter(Boolean);

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-4xl mx-auto" data-testid="member-profile">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5"
      >
        <ArrowLeft size={14} /> Back
      </button>

      {/* Hero */}
      <section className="bg-white border border-navy/10 rounded-xl overflow-hidden shadow-sm">
        <div className="h-24 bg-gradient-to-r from-navy via-navy-light to-saffron/40" />
        <div className="px-6 sm:px-8 pb-7 -mt-12 sm:-mt-14">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full ring-4 ring-white bg-pearl text-saffron text-3xl font-display flex items-center justify-center overflow-hidden flex-shrink-0">
              {photo
                ? <img src={photo} alt={p.name || ""} className="w-full h-full object-cover" />
                : (p.name || "M").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1 pb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-2xl sm:text-3xl text-navy leading-tight">{p.name || "Member"}</h1>
                <TierBadge tier={p.membership_tier} role={p.role} />
                {p.voting_eligible && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/15 text-emerald-700 rounded font-medium uppercase tracking-wider">
                    Voting member
                  </span>
                )}
              </div>
              {(p.pronouns || p.wing) && (
                <div className="text-navy/60 text-sm mt-1">
                  {p.pronouns && <span>{p.pronouns}</span>}
                  {p.pronouns && p.wing && <span> · </span>}
                  {p.wing && <span>{p.wing}</span>}
                </div>
              )}
              {(p.city || p.country) && (
                <div className="text-navy/55 text-sm mt-1 flex items-center gap-1">
                  <MapPin size={12} /> {[p.city, p.country].filter(Boolean).join(", ")}
                </div>
              )}
              {p.joined_at && (
                <div className="text-navy/45 text-xs mt-1">
                  Member since {fmtJoined(p.joined_at)} · ID {p.membership_id}
                </div>
              )}
            </div>
            {isMe && (
              <Link
                to="/platform/profile/edit"
                data-testid="member-profile-edit-link"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-navy text-pearl rounded-md text-xs hover:bg-navy-light"
              >
                <Pencil size={13} /> Edit profile
              </Link>
            )}
          </div>

          {(p.badges && p.badges.length > 0) && (
            <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="member-profile-badges">
              <BadgeRow badges={p.badges} size="lg" />
            </div>
          )}

          {p.bio && (
            <p className="mt-5 text-navy/80 leading-relaxed font-serif whitespace-pre-wrap text-[15px] max-w-2xl">
              {p.bio}
            </p>
          )}

          {(p.skills?.length > 0 || p.interests?.length > 0) && (
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              {p.skills?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-navy/55 mb-1.5">Skills · how I serve</div>
                  <div className="flex flex-wrap gap-1.5">
                    {p.skills.map((s, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 bg-saffron/10 text-saffron border border-saffron/30 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {p.interests?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-navy/55 mb-1.5">Interests</div>
                  <div className="flex flex-wrap gap-1.5">
                    {p.interests.map((s, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 bg-navy/5 text-navy/75 border border-navy/15 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {social.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-3">
              {social.map(({ Icon, href, label }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-navy/65 hover:text-saffron"
                >
                  <Icon size={14} /> {label}
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Threads */}
      <section className="mt-8" data-testid="member-profile-threads">
        <h2 className="font-display text-xl text-navy mb-3">
          {threads.length} {threads.length === 1 ? "discussion" : "discussions"}
        </h2>
        {threads.length === 0 ? (
          <div className="bg-white border border-navy/10 rounded-xl p-8 text-center text-navy/55 text-sm">
            <MessageSquare size={22} className="mx-auto mb-2 text-saffron" />
            No discussions yet.
          </div>
        ) : (
          <div className="bg-white border border-navy/10 rounded-xl divide-y divide-navy/5 overflow-hidden">
            {threads.map((t) => (
              <Link
                key={t.thread_id}
                to={`/platform/community/${t.thread_id}`}
                className="block p-4 hover:bg-pearl/40"
              >
                <div className="font-display text-base text-navy leading-tight">{t.title}</div>
                <div className="text-xs text-navy/55 mt-1">
                  {t.category} · {t.reply_count || 0} replies · {fmtJoined(t.last_activity_at || t.created_at)}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Edit
// ──────────────────────────────────────────────────────────────────────────────
const WINGS = ["", "Youth Wing", "Women's Wing", "Sangha Council", "Volunteer Team"];

export function MemberProfileEdit() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    bio: "", pronouns: "", wing: "",
    interests: [], skills: [],
    website: "", social_twitter: "", social_linkedin: "", social_instagram: "",
    directory_opt_out: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [interestInput, setInterestInput] = useState("");

  // Load current values from /community/profile/{me} for self
  useEffect(() => {
    if (!user?.membership_id) return;
    api.get(`/community/profile/${encodeURIComponent(user.membership_id)}`)
      .then((r) => {
        const p = r.data?.profile || {};
        setForm({
          bio: p.bio || "",
          pronouns: p.pronouns || "",
          wing: p.wing || "",
          interests: p.interests || [],
          skills: p.skills || [],
          website: p.website || "",
          social_twitter: p.social_twitter || "",
          social_linkedin: p.social_linkedin || "",
          social_instagram: p.social_instagram || "",
          directory_opt_out: !!p.directory_opt_out,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.membership_id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addTo = (key, val, setter) => {
    const v = val.trim();
    if (!v) return;
    if (form[key].includes(v)) { setter(""); return; }
    if (form[key].length >= 12) { toast.error("Maximum 12 entries"); return; }
    update(key, [...form[key], v]);
    setter("");
  };
  const removeFrom = (key, idx) => update(key, form[key].filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/me/profile-extras", form);
      toast.success("Profile updated");
      if (refresh) await refresh();
      if (user?.membership_id) {
        navigate(`/platform/profile/${user.membership_id}`);
      } else {
        navigate("/platform/community/members");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-12 text-center text-navy/60">Loading…</div>;

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-2xl mx-auto" data-testid="member-profile-edit">
      <Link to={user?.membership_id ? `/platform/profile/${user.membership_id}` : "/platform/community/members"}
            className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy mb-5">
        <ArrowLeft size={14} /> Back to profile
      </Link>
      <h1 className="font-display text-3xl text-navy">Edit your profile</h1>
      <p className="text-navy/60 text-sm mt-1">
        Tell the Sangha a little about yourself. All fields are optional.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-5 bg-white border border-navy/10 rounded-xl p-6">
        <label className="block">
          <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Pronouns</span>
          <input value={form.pronouns} onChange={(e) => update("pronouns", e.target.value)}
                 placeholder="e.g. she/her, he/him, they/them"
                 maxLength={40} className="form-input w-full" data-testid="profile-edit-pronouns" />
        </label>

        <label className="block">
          <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Wing / Council</span>
          <select value={form.wing} onChange={(e) => update("wing", e.target.value)}
                  className="form-input w-full" data-testid="profile-edit-wing">
            {WINGS.map((w) => <option key={w} value={w}>{w || "— None —"}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Bio</span>
          <textarea value={form.bio} onChange={(e) => update("bio", e.target.value)}
                    rows={5} maxLength={1500}
                    placeholder="Share your Dhamma journey, what brought you to the Sangha, how you serve…"
                    className="form-input w-full font-serif" data-testid="profile-edit-bio" />
          <span className="text-[10px] text-navy/45 block text-right mt-0.5">{form.bio.length} / 1500</span>
        </label>

        <ChipField
          label="Skills · how I serve"
          placeholder="e.g. translation, event MC, youth work…"
          value={skillInput}
          setValue={setSkillInput}
          chips={form.skills}
          onAdd={() => addTo("skills", skillInput, setSkillInput)}
          onRemove={(i) => removeFrom("skills", i)}
          testId="profile-edit-skills"
          chipColor="bg-saffron/10 text-saffron border-saffron/30"
        />

        <ChipField
          label="Interests"
          placeholder="e.g. Pali canon, meditation, social justice…"
          value={interestInput}
          setValue={setInterestInput}
          chips={form.interests}
          onAdd={() => addTo("interests", interestInput, setInterestInput)}
          onRemove={(i) => removeFrom("interests", i)}
          testId="profile-edit-interests"
          chipColor="bg-navy/5 text-navy/75 border-navy/15"
        />

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Website</span>
            <input value={form.website} onChange={(e) => update("website", e.target.value)}
                   placeholder="https://example.org"
                   maxLength={200} className="form-input w-full" data-testid="profile-edit-website" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Twitter / X handle</span>
            <input value={form.social_twitter} onChange={(e) => update("social_twitter", e.target.value)}
                   placeholder="ambedkarsociety"
                   maxLength={80} className="form-input w-full" data-testid="profile-edit-twitter" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">LinkedIn</span>
            <input value={form.social_linkedin} onChange={(e) => update("social_linkedin", e.target.value)}
                   placeholder="username or full URL"
                   maxLength={200} className="form-input w-full" data-testid="profile-edit-linkedin" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">Instagram handle</span>
            <input value={form.social_instagram} onChange={(e) => update("social_instagram", e.target.value)}
                   placeholder="ambedkarsociety"
                   maxLength={80} className="form-input w-full" data-testid="profile-edit-instagram" />
          </label>
        </div>

        <label className="flex items-start gap-2.5 mt-2 cursor-pointer">
          <input type="checkbox" checked={form.directory_opt_out}
                 onChange={(e) => update("directory_opt_out", e.target.checked)}
                 className="mt-1" data-testid="profile-edit-optout" />
          <span className="text-sm text-navy/75">
            <strong>Hide me from the public Member Directory.</strong>{" "}
            Your name will still appear on threads you've started.
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Link to={user?.membership_id ? `/platform/profile/${user.membership_id}` : "/platform"}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-navy/65 hover:text-navy text-sm">
            Cancel
          </Link>
          <button type="submit" disabled={saving}
                  className="inline-flex items-center gap-1.5 px-5 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light disabled:opacity-40"
                  data-testid="profile-edit-save">
            <Save size={14} /> {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ChipField({ label, placeholder, value, setValue, chips, onAdd, onRemove, testId, chipColor }) {
  return (
    <div>
      <span className="block text-xs uppercase tracking-widest text-navy/60 mb-1">{label}</span>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); onAdd(); }
          }}
          placeholder={placeholder}
          maxLength={40}
          className="form-input flex-1"
          data-testid={`${testId}-input`}
        />
        <button type="button" onClick={onAdd}
                className="inline-flex items-center gap-1 px-3 py-2 bg-navy/5 hover:bg-navy/10 text-navy text-xs rounded-md"
                data-testid={`${testId}-add`}>
          <Plus size={13} /> Add
        </button>
      </div>
      {chips.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {chips.map((s, i) => (
            <span key={i} className={`text-xs px-2 py-0.5 ${chipColor} border rounded-full inline-flex items-center gap-1`}>
              {s}
              <button type="button" onClick={() => onRemove(i)} className="hover:text-red-700" aria-label={`remove ${s}`}>
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
